import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import TablePager from "../Users/components/TablePager";
import AffiliateLayout, { SETTLE_STATUS, affiliateName, inr, paymentModeLabel } from "./components/AffiliateLayout";
import PayoutDrawer from "./components/PayoutDrawer";
import AddPayoutModal from "./modal/AddPayoutModal";
import PayoutStatusModal, { PayoutStatusChange } from "./modal/PayoutStatusModal";

// Interfaces
interface StatItem {
    value: number;
    growth: number;
}

interface PayoutStats {
    totalPayout: StatItem;
    avgPayout: StatItem;
    pendingPayout: StatItem;
    failedPayout: StatItem;
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

const AffiliatePayout = () => {
    const [searchParams] = useSearchParams();
    const urlSearch = searchParams.get("search") || "";

    const [payoutList, setPayoutList] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalPayouts, setTotalPayouts] = useState<number>(0);
    const [isLoader, setIsLoader] = useState<boolean>(true);

    // Drawer & Modals
    const [viewId, setViewId] = useState<string | null>(null);
    const [drawerRefresh, setDrawerRefresh] = useState(0);
    const [showAddModal, setShowAddModal] = useState(false);
    const [statusChange, setStatusChange] = useState<PayoutStatusChange | null>(null);

    // Stats
    const [stats, setStats] = useState<PayoutStats>({
        totalPayout: EMPTY_STAT,
        avgPayout: EMPTY_STAT,
        pendingPayout: EMPTY_STAT,
        failedPayout: EMPTY_STAT,
    });
    const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, paid: 0, failed: 0 });

    // Filters (a link from an affiliate drawer searches for that affiliate across all time)
    const [period, setPeriod] = useState(urlSearch ? "all" : "month");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [searchText, setSearchText] = useState(urlSearch);
    const [searchQuery, setSearchQuery] = useState(urlSearch);
    const [statusFilter, setStatusFilter] = useState("");

    // Fetch Payout List (list + stats follow the selected period)
    const getPayoutList = useCallback(async () => {
        const [from, to] = periodRange(period, customFrom, customTo);
        if (period === "custom" && (!from || !to)) return;
        const startDate = from ? from.format('YYYY-MM-DD') : "";
        const endDate = to ? to.format('YYYY-MM-DD') : "";
        try {
            setIsLoader(true);
            const response = await getRequest(
                `/affiliate/affiliate-payout-list?page=${page}&limit=${pageSize}&search=${encodeURIComponent(searchQuery)}&status=${statusFilter}&startDate=${startDate}&endDate=${endDate}`
            );

            if (response.status === "success") {
                setPayoutList(response.data);
                setTotalPages(response.pagination.totalPages || 1);
                setTotalPayouts(response.pagination.total);
                if (response.stats) setStats(response.stats);
                if (response.statusCounts) setStatusCounts(response.statusCounts);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading payouts!");
        } finally {
            setIsLoader(false);
        }
    }, [page, pageSize, searchQuery, statusFilter, period, customFrom, customTo]);

    useEffect(() => {
        getPayoutList();
    }, [getPayoutList]);

    // Debounced Search
    useEffect(() => {
        const delay = setTimeout(() => {
            setSearchQuery(searchText);
            setPage(1);
        }, 500);
        return () => clearTimeout(delay);
    }, [searchText]);

    // after a status change / new payout: reload the list and the open drawer
    const refreshAll = () => {
        getPayoutList();
        setDrawerRefresh(n => n + 1);
    };

    const changeStatus = (s: string) => { setStatusFilter(s); setPage(1); };

    const resetFilters = () => {
        setSearchText("");
        changeStatus("");
    };

    const chips: [string, string, number][] = [
        ["", "All", statusCounts.all],
        ["0", "Pending", statusCounts.pending],
        ["1", "Paid", statusCounts.paid],
        ["2", "Failed", statusCounts.failed],
    ];

    // All time has no previous period, so no growth %
    const isAll = period === "all";

    return (
        <AffiliateLayout
            loading={isLoader}
            actions={<>
                <PeriodFilter
                    allTime
                    value={period}
                    onChange={(p) => { setPeriod(p); setPage(1); }}
                    from={customFrom}
                    to={customTo}
                    onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
                />
                {/* <button type="button" className="cx-btn cx-btn-primary" onClick={() => setShowAddModal(true)}>
                    <i className="ri-add-line"></i>Create payout
                </button> */}
            </>}
            stats={[
                { label: "Paid out", value: inr(stats.totalPayout.value, 0), icon: "ri-checkbox-circle-line", delta: isAll ? "All time" : stats.totalPayout.growth },
                { label: "Pending payouts", value: inr(stats.pendingPayout.value, 0), icon: "ri-time-line", delta: `${statusCounts.pending} to transfer` },
                { label: "Failed payouts", value: inr(stats.failedPayout.value, 0), icon: "ri-error-warning-line", delta: isAll ? "All time" : stats.failedPayout.growth, invert: true },
                { label: "Avg paid payout", value: inr(stats.avgPayout.value, 0), icon: "ri-scales-3-line", delta: isAll ? "All time" : stats.avgPayout.growth },
            ]}
        >
            <div className="cx-card">
                <div className="cx-toolbar">
                    <div className="cx-chips">
                        {chips.map(([k, l, n]) => (
                            <button type="button" key={l} className={`cx-chip ${statusFilter === k ? "on" : ""}`} onClick={() => changeStatus(k)}>
                                {l} <b>{(n || 0).toLocaleString("en-IN")}</b>
                            </button>
                        ))}
                    </div>
                    <div className="sp"></div>
                    <input
                        className="cx-input"
                        type="search"
                        placeholder="Search affiliate, email, payout ID…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
                        <i className="ri-refresh-line"></i>Reset
                    </button>
                </div>

                <div className="cx-tbl-wrap">
                    <table className="cx-tbl">
                        <thead>
                            <tr>
                                <th>Payout</th>
                                <th>Affiliate</th>
                                <th className="c">Commissions</th>
                                <th className="r">Amount</th>
                                <th>Status</th>
                                <th>Remark</th>
                                <th>Created</th>
                                <th className="r">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payoutList && payoutList.length > 0 ? (
                                payoutList.map((item) => {
                                    const [tone, label] = SETTLE_STATUS[item.ap_status] || ["none", "Unknown"];
                                    return (
                                        <tr key={item._id} className="click" onClick={() => setViewId(item._id)}>
                                            <td>
                                                <span className="mono">#{item.ap_unique_id || item.ap_id}</span>
                                                <div className="fs12 muted">
                                                    {(item.ap_remark || "").startsWith("Requested by affiliate")
                                                        ? "Affiliate request"
                                                        : item.ap_week != null ? `Week ${item.ap_week}, ${item.ap_year}` : "Manual"}
                                                </div>
                                            </td>
                                            <td>
                                                <b style={{ fontWeight: 600 }}>{affiliateName(item)}</b>
                                                <div className="fs12 muted">{item.affiliate_email || ""}</div>
                                            </td>
                                            <td className="c mono">{item.commission_count ?? "—"}</td>
                                            <td className="r mono" style={{ fontWeight: 600 }}>{inr(item.ap_amount)}</td>
                                            <td>
                                                <Pill tone={tone}>{label}</Pill>
                                                {item.ap_status === 1 && item.ap_payment_mode && (
                                                    <div className="fs12 muted" title={item.ap_payment_reference || ""}>
                                                        {paymentModeLabel(item.ap_payment_mode)}{item.ap_paid_at ? ` · ${moment(item.ap_paid_at).format("DD MMM")}` : ""}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="fs12 muted" title={item.ap_remark || ""} style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {item.ap_remark || "—"}
                                            </td>
                                            <td className="fs12 muted">{moment(item.createdAt).format("DD MMM YYYY")}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="cx-ra">
                                                    <button type="button" className="v" title="View payout" onClick={() => setViewId(item._id)}>
                                                        <i className="ri-eye-line"></i>
                                                    </button>
                                                    {item.ap_status === 0 && <>
                                                        <button type="button" title="Mark paid" onClick={() => setStatusChange({ payout: item, status: 1 })}>
                                                            <i className="ri-check-line"></i>
                                                        </button>
                                                        <button type="button" title="Mark failed" onClick={() => setStatusChange({ payout: item, status: 2 })}>
                                                            <i className="ri-close-line"></i>
                                                        </button>
                                                    </>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="cx-empty">
                                            <i className="ri-bank-line"></i>
                                            {isLoader ? "Loading…" : <>No payouts match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button></>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <TablePager
                    page={page}
                    totalPages={totalPages}
                    total={totalPayouts}
                    shown={payoutList.length}
                    pageSize={pageSize}
                    label="payouts"
                    note="Affiliates request payouts (min ₹500). Transfer pending requests to their bank, then mark them paid"
                    setPage={setPage}
                    setPageSize={setPageSize}
                />
            </div>

            <PayoutDrawer
                payoutId={viewId}
                refreshKey={drawerRefresh}
                onClose={() => setViewId(null)}
                onChangeStatus={(payout, status) => setStatusChange({ payout, status })}
            />

            {/* Modals */}
            <AddPayoutModal show={showAddModal} onHide={() => setShowAddModal(false)} onSuccess={refreshAll} />
            <PayoutStatusModal change={statusChange} onHide={() => setStatusChange(null)} onSuccess={refreshAll} />
        </AffiliateLayout>
    );
};

export default AffiliatePayout;
